import ApiService from "@/services/api.service";
import logger from "@/utils/logger";
import { sendWhatsApp } from "@/services/whatsappNotification.service";
import { sendCompanyNotificationEmail } from "@/services/companyEmailNotification.service";
import {
  formatMessageDateTime,
  formatMessageDisplayCalendarDate,
} from "@/services/consultantOnboardingNotification.helpers.js";
import { buildTimesheetCreatedNotificationEmailHtml } from "@/utils/companyNotificationEmailHtml.js";

/**
 * Whether Settings → Messaging allows SMS alongside WhatsApp for this company.
 */
export async function fetchCompanyMessagingSendSmsAlso(companyId) {
  if (companyId == null || companyId === "") return false;
  try {
    const res = await ApiService.get(
      `/company/${encodeURIComponent(String(companyId))}/messaging-settings`
    );
    const row = res.data?.data || res.data;
    const whatsappOn = row?.whatsappNotificationsEnabled !== false;
    const smsOn = Boolean(row?.smsNotificationsEnabled);
    return whatsappOn && smsOn;
  } catch (e) {
    logger.warn("fetchCompanyMessagingSendSmsAlso", e);
    return false;
  }
}

export function resolveTimesheetConsultantLabel({
  isConsultantPrefilled,
  displayConsultantName,
  selectedConsultantName,
  consultant,
}) {
  if (isConsultantPrefilled) {
    return String(displayConsultantName || "").trim() || "—";
  }
  return (
    String(selectedConsultantName || "").trim() ||
    (consultant && String(consultant.consultantName || consultant.name || "").trim()) ||
    "—"
  );
}

export function buildTimesheetCreatedMessage({
  consultantName,
  fromDate,
  toDate,
  hours,
  status,
  createdAt,
  companyName,
  customerName,
  vendorName,
}) {
  const fromLabel = formatMessageDisplayCalendarDate(fromDate) || fromDate || "—";
  const toLabel = formatMessageDisplayCalendarDate(toDate) || toDate || "—";
  const lines = [];
  const co = String(companyName || "").trim();
  if (co) lines.push(`Company: ${co}`);
  const cust = String(customerName || "").trim();
  if (cust) lines.push(`Customer: ${cust}`);
  const vend = String(vendorName || "").trim();
  if (vend) lines.push(`Vendor: ${vend}`);
  lines.push(`Consultant: ${consultantName || "—"}`);
  lines.push(`Period: ${fromLabel} – ${toLabel}`);
  lines.push(`Hours: ${hours != null && hours !== "" ? String(hours) : "—"}`);
  lines.push(`Status: ${status || "Time Sheet Created"}`);
  if (createdAt) {
    lines.push(`Recorded at: ${createdAt}`);
  }
  return lines.join("\n");
}

/**
 * Notify company messaging recipients after a timesheet is created (same API as consultant onboarding).
 */
export async function notifyTimesheetCreated({
  consultantName,
  fromDate,
  toDate,
  hours,
  status,
  companyId,
  sendSMS = false,
  companyName,
  customerName,
  vendorName,
}) {
  const createdAt = formatMessageDateTime();
  const notifyPayload = {
    consultantName,
    fromDate,
    toDate,
    hours,
    status,
    createdAt,
    companyName,
    customerName,
    vendorName,
  };
  const message = buildTimesheetCreatedMessage({
    ...notifyPayload,
    createdAt,
  });
  try {
    await sendWhatsApp(message, Boolean(sendSMS), {
      ...(companyId != null && companyId !== ""
        ? { companyId: String(companyId) }
        : {}),
    });
  } catch {
    /* errors logged in sendWhatsApp */
  }
  await sendCompanyNotificationEmail(message, companyId, "Anna Books: New timesheet", {
    event: "timesheet",
    bannerTitle: consultantName || "—",
    html: buildTimesheetCreatedNotificationEmailHtml(notifyPayload),
  });
}

/**
 * Loads company messaging flags, resolves consultant label, sends timesheet-created notification.
 */
export async function notifyTimesheetCreatedWithCompanyMessaging({
  isConsultantPrefilled,
  displayConsultantName,
  selectedConsultantName,
  consultant,
  fromDate,
  toDate,
  hours,
  status,
  companyId,
  companyName,
  customerName,
  vendorName,
}) {
  const sendSMS = await fetchCompanyMessagingSendSmsAlso(companyId);
  const consultantName = resolveTimesheetConsultantLabel({
    isConsultantPrefilled,
    displayConsultantName,
    selectedConsultantName,
    consultant,
  });
  await notifyTimesheetCreated({
    consultantName,
    fromDate,
    toDate,
    hours,
    status,
    companyId,
    sendSMS,
    companyName,
    customerName,
    vendorName,
  });
}
